import { InfoOutlined } from '@mui/icons-material';
import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getGiftStats } from 'src/shared/services/giftService';
import { HeaderSubTitle } from 'src/modules/dashboard/components/atoms/headerSubTitle';
import { WidgetInstance } from '../types';
import { currentPeriodSub, formatPeriodSubLabel, Period, periodRange, previousPeriodRange, TrendChip } from './periodHelpers';

export type GiftsByPeriodConfig = {
  period: Period;
};

type Props = {
  instance: WidgetInstance;
};

export const GiftsByPeriodWidget = ({ instance }: Props) => {
  const { t } = useTranslation(['dashboard']);
  const config = (instance.config as GiftsByPeriodConfig | undefined) ?? { period: 'thisMonth' };
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [total, setTotal] = useState<number>();
  const [previousTotal, setPreviousTotal] = useState<number>();

  useEffect(() => {
    setLoading(true);
    setHasError(false);
    const currentRange = periodRange(config.period);
    const prevRange = previousPeriodRange(config.period);
    getGiftStats(currentRange.from, currentRange.to, prevRange.from, prevRange.to)
      .then((res) => {
        if (!isMounted()) return;
        setTotal(res.data.totalAmount);
        setPreviousTotal(res.data.previousTotalAmount);
      })
      .catch((e) => {
        ConsoleHelper.error(e);
        if (isMounted()) setHasError(true);
      })
      .finally(() => {
        if (isMounted()) setLoading(false);
      });
  }, [config.period, isMounted]);

  return (
    <Stack sx={{ p: 2, height: '100%' }} spacing={1.25}>
      <Stack spacing={0.25}>
        <Stack direction='row' alignItems='center' spacing={0.5}>
          <HeaderSubTitle label={t('dashboard:giftsByPeriod.header')} />
          <Tooltip arrow title={t('dashboard:giftsByPeriod.info')}>
            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help', mb: 0.5 }} />
          </Tooltip>
        </Stack>
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
          {t(`dashboard:period.labels.${config.period}`)} ({formatPeriodSubLabel(currentPeriodSub(config.period), t)})
        </Typography>
      </Stack>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
        {(() => {
          if (loading) return <Skeleton variant='text' sx={{ fontSize: 36, width: '60%' }} />;
          if (hasError) return <Typography sx={{ color: 'error.main', fontSize: 13 }}>{t('dashboard:fetchError')}</Typography>;
          return (
            <>
              <Typography sx={{ fontSize: 38, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em' }}>
                {(total ?? 0).toLocaleString('sv-SE')}
                <Typography component='span' sx={{ fontSize: 14, color: 'text.secondary', fontWeight: 400, ml: 0.75 }}>
                  {t('dashboard:giftsByPeriod.currencySuffix')}
                </Typography>
              </Typography>
              <TrendChip current={total ?? 0} previous={previousTotal ?? 0} period={config.period} />
            </>
          );
        })()}
      </Box>
    </Stack>
  );
};
